/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	Inject,
	OnDestroy,
	OnInit,
	ViewChild,
} from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { LocationsApi } from 'src/app/app-data/state/LocationsApi';
import { DefaultSplitDataRenderType, Location } from '@touchplan/common';
import { sortArray } from 'src/app/plain-javascript/services/rxjs-internal-extensions';
import { of, Observable } from 'rxjs';
import { takeUntil, map } from 'rxjs/operators';

import { sortLocationsByName } from 'src/app/utils/array-util';
import { GenericDialogComponent } from 'src/app/common/generic-dialog/generic-dialog.component';
import { ColorPickerComponent } from 'src/app/common/color-picker/color-picker.component';
import {
	AbstractItemListEditorComponent,
	IListItemData,
	IListItemEventData,
	ItemType,
} from 'src/app/common/dialog-reusable/dialog-reusable.module';
import { ColorUtils } from 'src/app/utils/color-utils';
import { ColorsApi } from 'src/app/app-data/state/ColorsApi';

export interface ILocationDialogData {
	locationsApi: LocationsApi;
	locationColorApi: ColorsApi;
	isAdmin: boolean;
}

@Component({
	selector: 'project-location-dialog',
	templateUrl: './project-location-dialog.component.html',
	styleUrls: ['./project-location-dialog.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProjectLocationDialogComponent
	extends AbstractItemListEditorComponent<Location>
	implements OnInit, OnDestroy
{
	@ViewChild('colorPicker', { static: false }) public colorPicker: ColorPickerComponent;

	public locationsApi: LocationsApi;
	public locationColorApi: ColorsApi;

	constructor(
		@Inject(MAT_DIALOG_DATA) public data: ILocationDialogData,
		protected dialogRef: MatDialogRef<ProjectLocationDialogComponent>,
		protected changeDetectorRef: ChangeDetectorRef,
		protected dialog: MatDialog
	) {
		super(changeDetectorRef, dialogRef);

		this.locationsApi = data.locationsApi;
		this.locationColorApi = data.locationColorApi;
		this.isAdmin = data.isAdmin;

		this.self = this;
		this.itemType = {
			type: ItemType.Location,
			singularDisplay: this.strings.PROJECT_ITEM_LOCATION_SINGULAR,
			pluralDisplay: this.strings.PROJECT_ITEM_LOCATION_PLURAL,
			icon: 'location_on',
		};
	}

	public ngOnInit() {
		super.ngOnInit();
	}

	public ngOnDestroy() {
		super.ngOnDestroy();
	}

	public completeAdd(emittedData: IListItemEventData<Location>): boolean {
		let success = true;

		if (emittedData.type === 'Save') {
			const formData = emittedData.data;

			// location name needs to be unique
			if (this.hasLocationName(formData['name'])) {
				this.showUniquenessDialog();

				success = false;
			} else {
				const data = {
					name: formData['name'],
					locationColor: this.getLocationColorId(formData['color']),
				};

				this.locationsApi.addItemLegacy(data);
			}
		}

		if (success) {
			emittedData.form?.reset();
		}

		return success;
	}

	public completeEdit(emittedData: IListItemEventData<Location>): boolean {
		let success = true;

		if (emittedData.type === 'Delete') {
			this.locationsApi.removeItem(this.itemInEdit);
		} else if (emittedData.type === 'Save') {
			const formData = emittedData.data;
			// location name needs to be unique
			if (this.hasLocationName(formData['name'], emittedData.item)) {
				this.showUniquenessDialog();

				success = false;
			} else {
				const data = {
					name: formData['name'],
					locationColor: this.getLocationColorId(formData['color']),
				};

				this.locationsApi.localUpdate(this.itemInEdit.$id, data);
				this.locationsApi.persistData(this.itemInEdit, DefaultSplitDataRenderType.database);
			}
		}

		return success;
	}

	protected createItemListObservable(): Observable<Location[]> {
		// sort members alphabatically by name
		return this.locationsApi.data.list$.pipe(
			map((roleMap) => Array.from(roleMap.values())),
			sortArray(of(sortLocationsByName)),
			takeUntil(this.shutdownSubject$)
		);
	}

	public getListItemData(location: Location): IListItemData {
		if (location) {
			return {
				leftLogo: {
					type: 'color',
					value: location.view.color || null,
				},
				middleContent: [location.view && location.view.name ? location.view.name : ''],
			};
		} else {
			return null;
		}
	}

	public doesItemMatchSearch(location: Location, searchText: string): boolean {
		return (location.view && location.view.name ? location.view.name : '')
			.toLowerCase()
			.includes(searchText.toLowerCase());
	}

	protected createItemPaletteColors(): Observable<string[][]> {
		return this.locationColorApi.data.list$.pipe(
			map((colorMap) => {
				const colors: string[] = [];
				colorMap.forEach((val) => colors.push(val.view.color));
				return [colors];
			}),
			takeUntil(this.shutdownSubject$)
		);
	}

	protected createUsedColors(): Observable<{}> {
		return this.locationsApi.data.list$.pipe(
			map((locationMap) => {
				const used = {};
				locationMap.forEach((location) => {
					const colorKey = location.view.locationColor;
					if (colorKey) {
						const color = location.view.color;
						if (used[color]) {
							used[color] = used[color] + 1;
						} else {
							used[color] = 1;
						}
					}
				});
				return used;
			}),
			takeUntil(this.shutdownSubject$)
		);
	}

	protected addColor(color: string): void {
		this.locationColorApi.addColor(color);
		super.addColor(color);
	}

	private getLocationColorId(color: string): string {
		let colorId = null;
		const syncColorList = this.locationColorApi.data.syncList;

		syncColorList.forEach((syncColor) => {
			let savedColor = syncColor.view.color;

			// old saved color could be in rgb format
			if (savedColor.includes('rgb')) {
				savedColor = ColorUtils.rgbToHex(savedColor)!;
			}

			if (color === savedColor) {
				colorId = syncColor.$id;
			}
		});

		return colorId;
	}

	private hasLocationName(name: string, item: Location = null): boolean {
		// exclude item itself when comparing for edit case
		return !!Array.from(this.locationsApi.data.syncList.values()).find(
			(location) =>
				location !== item && location.view.name && location.view.name.toLowerCase() === name.toLowerCase()
		);
	}

	private showUniquenessDialog() {
		this.dialog.open(GenericDialogComponent, {
			data: {
				title: this.strings.LOCATION_NAME_UNIQUE_TEXT,
				yesButtonText: this.strings.UICONTROL_TEXT_OK,
			},
			disableClose: true,
		});
	}
}
